/**
 * ProgramImpactPage (Admin_Program_Aggregate_Page)
 *
 * Sponsor-facing roll-up of Instagram impact across the program for the
 * current season, with a print-friendly layout suitable for sponsor decks.
 *
 * Requirements: 8.1 - 8.18
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import {
  getProgramAggregateInsights,
  getProgramReachTimeseries,
  getProgramPostsPerWeek,
  listSeasonGrowthSeries,
} from '@/graphql/queries';
import { AlertTriangle, ChevronDown, Printer } from 'lucide-react';
import CrownLoader from '@/components/ui/CrownLoader';
import { LegacyAvatar } from '@/components/ui/LegacyAvatar';
import { ProgramReachStackedArea } from '@/components/charts/ProgramReachStackedArea';
import { TopGrowthBarChart } from '@/components/charts/TopGrowthBarChart';
import { PostsPerWeekBarChart } from '@/components/charts/PostsPerWeekBarChart';
import { KpiTile } from '@/components/ui/kpi-tile';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import {
  USE_MOCK_SPOTLIGHT,
  MOCK_PROGRAM_AGGREGATE,
  MOCK_REACH_TIMESERIES,
  MOCK_POSTS_PER_WEEK,
  MOCK_GROWTH_SERIES,
} from '@/lib/spotlight/mockData';

const client = generateClient();

// ─── Types ──────────────────────────────────────────────────────────────────

interface TitleholderSummary {
  cognitoSub: string;
  fullName: string;
  title: string | null;
  profilePhotoS3Key: string | null;
  instagramUsername: string;
  followerCount: number;
}

interface Aggregate {
  competitionYear: number;
  contestantCount: number;
  titleholderCount: number;
  totalFollowersAcrossProgram: number;
  totalFollowersGainedContestantsOnly: number;
  totalPostsThisSeason: number;
  totalReachThisSeason: number;
  totalImpressionsThisSeason: number;
  generatedAt: string;
  lastSnapshotDate: string | null;
  titleholders: TitleholderSummary[];
}

interface ReachWeek {
  weekStartDate: string;
  contestantReach: number;
  titleholderReach: number;
}
interface ReachTimeseries {
  competitionYear: number;
  weeks: ReachWeek[];
}

interface PostsWeek {
  weekStartDate: string;
  totalPosts: number;
}
interface PostsPerWeek {
  competitionYear: number;
  weeks: PostsWeek[];
}

interface GrowthSeriesItem {
  cognitoSub: string;
  fullName: string;
  instagramUsername: string;
  points: { snapshotDate: string; value: number }[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ─── Season Selector (custom, no native <select>) ──────────────────────────

function SeasonSelector({
  value,
  onChange,
  options,
}: {
  value: number;
  onChange: (y: number) => void;
  options: number[];
}) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => [...options].sort((a, b) => b - a), [options]);
  return (
    <div className="relative" data-print-hidden="true">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-4 py-2 min-h-[var(--touch-target-min)] border border-gold-100 rounded font-sans text-xs tracking-[0.1em] uppercase text-gold-950 bg-card hover:bg-gold-50 transition-colors"
        data-testid="program-impact-season-selector"
      >
        <span>Season {value}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-1 w-40 border border-gold-100 rounded bg-card shadow-raised z-10"
        >
          {sorted.map((y) => (
            <li key={y}>
              <button
                type="button"
                role="option"
                aria-selected={y === value}
                onClick={() => {
                  onChange(y);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-2 min-h-[var(--touch-target-min)] font-sans text-xs tracking-[0.1em] uppercase transition-colors ${
                  y === value
                    ? 'bg-gold-950 text-gold-50'
                    : 'text-gold-950 hover:bg-gold-50'
                }`}
              >
                Season {y}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Stat card ──────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <KpiTile
      label={label}
      value={typeof value === 'number' ? formatNumber(value) : value}
    />
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function ProgramImpactPage() {
  // Season SSOT: seed the season selector from the active-season seam's
  // operative year rather than a clock-based `new Date().getFullYear()`.
  const { operativeYear } = useActiveSeason();
  const [competitionYear, setCompetitionYear] = useState<number>(operativeYear);
  const [aggregate, setAggregate] = useState<Aggregate | null>(null);
  const [reach, setReach] = useState<ReachTimeseries | null>(null);
  const [posts, setPosts] = useState<PostsPerWeek | null>(null);
  const [topGrowth, setTopGrowth] = useState<GrowthSeriesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [knownSeasons, setKnownSeasons] = useState<Set<number>>(
    new Set([operativeYear]),
  );

  const load = useCallback(async (year: number) => {
    setLoading(true);
    setError(null);

    // Dev-mock short-circuit.
    if (import.meta.env.DEV && USE_MOCK_SPOTLIGHT) {
      setAggregate(MOCK_PROGRAM_AGGREGATE as Aggregate);
      setReach(MOCK_REACH_TIMESERIES as ReachTimeseries);
      setPosts(MOCK_POSTS_PER_WEEK as PostsPerWeek);
      setTopGrowth(MOCK_GROWTH_SERIES.items.slice(0, 5) as GrowthSeriesItem[]);
      setKnownSeasons((prev) => new Set([...prev, year]));
      setLoading(false);
      return;
    }

    try {
      const [aggRes, reachRes, postsRes, topRes] = await Promise.all([
        client.graphql({
          query: getProgramAggregateInsights,
          variables: { competitionYear: year },
          authMode: 'userPool',
        }) as Promise<
          GraphQLResult<{ getProgramAggregateInsights: Aggregate }>
        >,
        client.graphql({
          query: getProgramReachTimeseries,
          variables: { competitionYear: year },
          authMode: 'userPool',
        }) as Promise<
          GraphQLResult<{ getProgramReachTimeseries: ReachTimeseries }>
        >,
        client.graphql({
          query: getProgramPostsPerWeek,
          variables: { competitionYear: year },
          authMode: 'userPool',
        }) as Promise<GraphQLResult<{ getProgramPostsPerWeek: PostsPerWeek }>>,
        client.graphql({
          query: listSeasonGrowthSeries,
          variables: {
            competitionYear: year,
            metric: 'followersGained',
            limit: 5,
          },
          authMode: 'userPool',
        }) as Promise<
          GraphQLResult<{ listSeasonGrowthSeries: { items: GrowthSeriesItem[] } }>
        >,
      ]);

      setAggregate(aggRes.data?.getProgramAggregateInsights ?? null);
      setReach(reachRes.data?.getProgramReachTimeseries ?? null);
      setPosts(postsRes.data?.getProgramPostsPerWeek ?? null);
      setTopGrowth(topRes.data?.listSeasonGrowthSeries?.items ?? []);
      setKnownSeasons((prev) => new Set([...prev, year]));
    } catch (err) {
      const msg =
        (err as Error)?.message ??
        'Unable to load program impact. Try again in a moment.';
      setError(msg);
      setAggregate(null);
      setReach(null);
      setPosts(null);
      setTopGrowth([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(competitionYear);
  }, [competitionYear, load]);

  const topGrowthEntries = useMemo(() => {
    return topGrowth.map((s) => ({
      fullName: s.fullName || s.instagramUsername,
      followersGained:
        s.points.length > 0 ? s.points[s.points.length - 1].value : 0,
    }));
  }, [topGrowth]);

  if (loading) {
    return (
      <div data-testid="program-impact-page">
        <CrownLoader fullPage message="Loading program impact…" />
      </div>
    );
  }

  return (
    <div data-testid="program-impact-page" className="admin-layout-main space-y-6">
      {/* Print CSS — scoped to this page. */}
      <style>{`
        @media print {
          @page { size: Letter portrait; margin: 0.5in; }
          aside[data-testid="admin-sidebar"] { display: none !important; }
          [data-print-hidden="true"] { display: none !important; }
          .admin-layout-main { padding-left: 0 !important; }
          body { background: #ffffff !important; }
        }
      `}</style>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl text-gold-950">Program Impact</h1>
          {aggregate && (
            <p
              className="text-eyebrow-sm text-gold-400 mt-1"
              data-testid="program-impact-meta"
            >
              Generated {formatDate(aggregate.generatedAt)}
              {aggregate.lastSnapshotDate
                ? ` · Last snapshot ${formatDate(aggregate.lastSnapshotDate)}`
                : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <SeasonSelector
            value={competitionYear}
            onChange={setCompetitionYear}
            options={Array.from(knownSeasons)}
          />
          <button
            type="button"
            onClick={() => window.print()}
            data-print-hidden="true"
            className="flex items-center gap-2 px-4 py-2 min-h-[var(--touch-target-min)] bg-gold-950 text-gold-50 text-eyebrow-sm font-extrabold hover:bg-gold-800 transition-colors rounded"
          >
            <Printer className="w-3.5 h-3.5" />
            Print
          </button>
        </div>
      </div>

      {error && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg px-5 py-4 flex items-center gap-3 text-red-800 text-sm"
          role="alert"
          data-testid="program-impact-error"
        >
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {aggregate && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard
              label="Contestants"
              value={aggregate.contestantCount}
            />
            <StatCard
              label="Titleholders"
              value={aggregate.titleholderCount}
            />
            <StatCard
              label="Total Followers Across Program"
              value={aggregate.totalFollowersAcrossProgram}
            />
            <StatCard
              label="Followers Gained This Season"
              value={aggregate.totalFollowersGainedContestantsOnly}
            />
            <StatCard
              label="Posts This Season"
              value={aggregate.totalPostsThisSeason}
            />
            <StatCard
              label="Reach This Season"
              value={aggregate.totalReachThisSeason}
            />
            <StatCard
              label="Impressions This Season"
              value={aggregate.totalImpressionsThisSeason}
            />
          </div>

          <ProgramReachStackedArea weeks={reach?.weeks ?? []} />

          <TopGrowthBarChart entries={topGrowthEntries} />

          <PostsPerWeekBarChart entries={posts?.weeks ?? []} />

          {/* State Titleholders section */}
          <div className="bg-card rounded-card shadow-subtle border border-gold-100 p-5">
            <h3 className="font-display text-lg text-gold-950 mb-4">
              State Titleholders
            </h3>
            {aggregate.titleholders.length === 0 ? (
              <p className="text-eyebrow-sm font-extrabold text-gold-500">
                No titleholders linked yet
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {aggregate.titleholders.map((t) => (
                  <div
                    key={t.cognitoSub}
                    className="flex items-center gap-3"
                    data-testid={`titleholder-${t.cognitoSub}`}
                  >
                    <LegacyAvatar
                      profilePhotoS3Key={t.profilePhotoS3Key}
                      fullName={t.fullName}
                      size="md"
                      variant="admin"
                    />
                    <div>
                      <div className="font-display text-gold-950">{t.fullName}</div>
                      {t.title && (
                        <div className="text-eyebrow-sm font-extrabold text-gold-500">
                          {t.title}
                        </div>
                      )}
                      <div className="font-sans text-xs text-gold-700 tabular-nums">
                        {formatNumber(t.followerCount)} followers
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ProgramImpactPage;
